import { cn } from "@newxsystems/shared";
import { RichTextHtml } from "../../RichTextHtml";
import { TootBlogPostCard } from "../../../../modules/content/components/TootBlogPostCard/TootBlogPostCard";
import { getTootSectionSpacing } from "../../../../modules/layout/toot-section-spacing";
import type { IFeaturedBlogPostsBlockProps } from "./FeaturedBlogPostsBlock.props";

export const FeaturedBlogPostsBlock = ({
  anchorId,
  emptyText,
  eyebrow,
  heading,
  parentClass,
  posts = [],
  spacing,
  subheading,
}: IFeaturedBlogPostsBlockProps) => {
  const [firstPost, ...remainingPosts] = posts;
  const hasSecondary = remainingPosts.length > 0;
  const headingId = heading && anchorId ? `${anchorId}-heading` : undefined;

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative overflow-hidden text-toot-foreground",
        getTootSectionSpacing(spacing),
        parentClass,
      )}
      id={anchorId ?? undefined}
    >
      <div className="toot-container">
        {eyebrow || heading || subheading ? (
          <header className="mb-10 max-w-4xl md:mb-14">
            {eyebrow ? <p className="toot-eyebrow mb-4">{eyebrow}</p> : null}
            {heading ? (
              <h2 className="toot-heading-section" id={headingId}>
                {heading}
              </h2>
            ) : null}
            {subheading ? (
              <RichTextHtml className="toot-subheading mt-5 max-w-2xl" html={subheading} />
            ) : null}
          </header>
        ) : null}
        {firstPost ? (
          <div
            className={cn(
              "grid gap-5 lg:gap-6",
              hasSecondary && "lg:grid-cols-[minmax(0,1.55fr)_minmax(19rem,0.85fr)] lg:grid-rows-2",
            )}
          >
            <TootBlogPostCard
              className={cn("min-h-[28rem] lg:min-h-[40rem]", hasSecondary && "lg:row-span-2")}
              post={firstPost}
              priority
              variant="featured"
            />
            {remainingPosts.slice(0, 2).map((post) => (
              <TootBlogPostCard
                className="min-h-[22rem]"
                key={post.id}
                post={post}
                variant="featuredCompact"
              />
            ))}
            {remainingPosts.length > 2 ? (
              <div className="grid gap-5 md:grid-cols-2 lg:col-span-2 lg:gap-6 xl:grid-cols-3">
                {remainingPosts.slice(2).map((post) => (
                  <TootBlogPostCard key={post.id} post={post} />
                ))}
              </div>
            ) : null}
          </div>
        ) : emptyText ? (
          <p className="rounded-[var(--toot-radius-card)] border border-dashed border-toot-grid-strong bg-card p-6 text-toot-muted">
            {emptyText}
          </p>
        ) : null}
      </div>
    </section>
  );
};

FeaturedBlogPostsBlock.displayName = "Block::FeaturedBlogPostsBlock";
